import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_LABELS,
  getUiTranslations,
  type SupportedLanguage,
} from "@/lib/ui-translations";

interface LanguageSwitcherProps {
  value: string;
  onChange: (next: SupportedLanguage) => void;
  /** Force compact mode (short code only) regardless of viewport. Default behavior is responsive: short code on phones, full name from sm (640px) up. */
  compact?: boolean;
}

export function LanguageSwitcher({ value, onChange, compact = false }: LanguageSwitcherProps) {
  const current = (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
    ? (value as SupportedLanguage)
    : 'fr';
  const currentLabel = LANGUAGE_LABELS[current];
  const currentCode = current.toUpperCase();
  // Localize the ARIA label so screen readers announce in the user's chosen language.
  const t = getUiTranslations(current);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg border border-border bg-card text-foreground hover:bg-secondary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t.changeLanguageAria(currentLabel)}
      >
        <Globe className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        {compact ? (
          <span className="text-sm font-medium">{currentCode}</span>
        ) : (
          <>
            <span className="text-sm font-medium sm:hidden">{currentCode}</span>
            <span className="text-sm font-medium hidden sm:inline">{currentLabel}</span>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onSelect={() => onChange(lang)}
            className="cursor-pointer min-h-[44px] text-base"
          >
            <span className="flex-1">{LANGUAGE_LABELS[lang]}</span>
            {lang === current && (
              <Check className="w-4 h-4 text-primary ml-2" aria-hidden="true" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
